import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Search, Check, X, AlertTriangle, Package, Clock, 
  CheckCircle, XCircle, Merge, ChevronDown, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const STATUS_TABS = [
  { id: 'PENDING', label: 'Pending', color: 'orange', icon: Clock },
  { id: 'APPROVED', label: 'Approved', color: 'green', icon: CheckCircle },
  { id: 'MERGED', label: 'Merged', color: 'purple', icon: Merge },
  { id: 'REJECTED', label: 'Rejected', color: 'red', icon: XCircle },
];

const IngredientProposals = () => {
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [statusCounts, setStatusCounts] = useState({
    PENDING: 0, APPROVED: 0, MERGED: 0, REJECTED: 0
  });
  
  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  
  // Form state
  const [approveAction, setApproveAction] = useState('approve_as_new');
  const [mergeIntoId, setMergeIntoId] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [existingIngredients, setExistingIngredients] = useState([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadProposals(activeTab);
    loadStatusCounts();
  }, [activeTab]);

  useEffect(() => {
    // Load existing ingredients for merge dropdown
    loadExistingIngredients();
  }, []);

  const loadProposals = async (status) => {
    setLoading(true);
    try {
      const response = await api.getIngredientProposals({ status, limit: 50 });
      setProposals(response.data?.proposals || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error loading proposals:', error);
      setProposals([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatusCounts = async () => {
    const counts = { PENDING: 0, APPROVED: 0, MERGED: 0, REJECTED: 0 };
    
    try {
      const results = await Promise.allSettled([
        api.getIngredientProposals({ status: 'PENDING', limit: 1 }),
        api.getIngredientProposals({ status: 'APPROVED', limit: 1 }),
        api.getIngredientProposals({ status: 'MERGED', limit: 1 }),
        api.getIngredientProposals({ status: 'REJECTED', limit: 1 }),
      ]);
      
      results.forEach((result, idx) => {
        const status = ['PENDING', 'APPROVED', 'MERGED', 'REJECTED'][idx];
        if (result.status === 'fulfilled') {
          counts[status] = result.value.data?.total || 0;
        }
      });
    } catch (error) {
      console.error('Error loading status counts:', error);
    }
    
    setStatusCounts(counts);
  };

  const loadExistingIngredients = async () => {
    try {
      const response = await api.getCanonicalIngredients({ limit: 500 });
      setExistingIngredients(response.data?.ingredients || []);
    } catch (error) {
      console.error('Error loading ingredients:', error);
    }
  };

  const openApproveModal = (proposal) => {
    setSelectedProposal(proposal);
    setApproveAction('approve_as_new');
    setMergeIntoId('');
    setIngredientSearch('');
    setShowApproveModal(true);
  };

  const openRejectModal = (proposal) => {
    setSelectedProposal(proposal);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleApprove = async () => {
    if (!selectedProposal) return;
    
    if (approveAction === 'merge_into_existing' && !mergeIntoId) {
      alert('Please select an ingredient to merge into');
      return;
    }
    
    setProcessing(true);
    try {
      await api.approveIngredientProposal(
        selectedProposal.proposal_id,
        approveAction,
        approveAction === 'merge_into_existing' ? mergeIntoId : null
      );
      
      setShowApproveModal(false);
      setSelectedProposal(null);
      loadProposals(activeTab);
      loadStatusCounts();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error approving proposal');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedProposal) return;
    
    if (!rejectionReason.trim()) {
      alert('Rejection reason is required');
      return;
    }
    
    setProcessing(true);
    try {
      await api.rejectIngredientProposal(selectedProposal.proposal_id, rejectionReason);
      
      setShowRejectModal(false);
      setSelectedProposal(null);
      loadProposals(activeTab);
      loadStatusCounts();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error rejecting proposal');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredIngredients = existingIngredients.filter(ing =>
    ing.ingredient_name.toLowerCase().includes(ingredientSearch.toLowerCase()) ||
    ing.aliases?.some(a => a.toLowerCase().includes(ingredientSearch.toLowerCase()))
  );

  // RBAC check
  if (!user?.is_org_admin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">Only organization administrators can manage ingredient proposals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="ingredient-proposals-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Ingredient Proposals</h1>
        <p className="text-gray-600 mt-1">Review and approve ingredient proposals from Vendor Engine</p>
      </div>

      {/* Status Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STATUS_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-4 rounded-lg border-2 transition-all ${
                isActive 
                  ? `border-${tab.color}-500 bg-${tab.color}-50` 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className={`text-xs font-medium ${isActive ? `text-${tab.color}-600` : 'text-gray-500'}`}>
                    {tab.label}
                  </p>
                  <p className={`text-2xl font-bold mt-1 ${isActive ? `text-${tab.color}-700` : 'text-gray-900'}`}>
                    {statusCounts[tab.id]}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${isActive ? `bg-${tab.color}-100` : 'bg-gray-100'}`}>
                  <Icon className={isActive ? `text-${tab.color}-600` : 'text-gray-400'} size={20} />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Proposals List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : proposals.length === 0 ? (
          <div className="p-12 text-center">
            {activeTab === 'PENDING' ? (
              <>
                <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                <p className="text-gray-600 text-lg">No pending proposals</p>
                <p className="text-sm text-gray-500 mt-1">All proposals have been processed</p>
              </>
            ) : (
              <>
                <Package className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600 text-lg">No {activeTab.toLowerCase()} proposals</p>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {proposals.map((proposal) => (
              <motion.div
                key={proposal.proposal_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {proposal.proposed_name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        proposal.status === 'PENDING' ? 'bg-orange-100 text-orange-800' :
                        proposal.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        proposal.status === 'MERGED' ? 'bg-purple-100 text-purple-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {proposal.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="text-gray-500">Category:</span>
                        <p className="font-medium">{proposal.proposed_category || 'Not specified'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Base Unit:</span>
                        <p className="font-medium">{proposal.proposed_base_unit || 'kg'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Source Item:</span>
                        <p className="font-medium">{proposal.source_item_name || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-xs text-gray-500">
                      Created: {formatDate(proposal.created_at)}
                      {proposal.approved_at && ` • Approved: ${formatDate(proposal.approved_at)}`}
                      {proposal.rejected_at && ` • Rejected: ${formatDate(proposal.rejected_at)}`}
                    </div>
                    
                    {proposal.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejection reason:</strong> {proposal.rejection_reason}
                      </div>
                    )}
                    
                    {proposal.merged_into_ingredient_id && (
                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-sm text-purple-700">
                        <strong>Merged into:</strong> {proposal.merged_into_ingredient_id}
                      </div>
                    )}
                  </div>
                  
                  {activeTab === 'PENDING' && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => openApproveModal(proposal)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <Check size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => openRejectModal(proposal)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <X size={16} />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Approve Modal */}
      <AnimatePresence>
        {showApproveModal && selectedProposal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowApproveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Approve Proposal</h3>
                <button onClick={() => setShowApproveModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Proposed:</strong> {selectedProposal.proposed_name}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Category: {selectedProposal.proposed_category || 'N/A'} • 
                  Unit: {selectedProposal.proposed_base_unit || 'kg'}
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="approveAction"
                      value="approve_as_new"
                      checked={approveAction === 'approve_as_new'}
                      onChange={(e) => setApproveAction(e.target.value)}
                      className="text-green-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Approve as New Ingredient</p>
                      <p className="text-xs text-gray-500">Create a new canonical ingredient</p>
                    </div>
                  </label>
                </div>
                
                <div>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="approveAction"
                      value="merge_into_existing"
                      checked={approveAction === 'merge_into_existing'}
                      onChange={(e) => setApproveAction(e.target.value)}
                      className="text-purple-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Merge into Existing</p>
                      <p className="text-xs text-gray-500">Add as alias to existing ingredient</p>
                    </div>
                  </label>
                </div>
                
                {approveAction === 'merge_into_existing' && (
                  <div className="pl-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Target Ingredient
                    </label>
                    <input
                      type="text"
                      placeholder="Search ingredients..."
                      value={ingredientSearch}
                      onChange={(e) => setIngredientSearch(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-purple-500"
                    />
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      {filteredIngredients.slice(0, 10).map((ing) => (
                        <label
                          key={ing.ingredient_id}
                          className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 ${
                            mergeIntoId === ing.ingredient_id ? 'bg-purple-50' : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="mergeTarget"
                            value={ing.ingredient_id}
                            checked={mergeIntoId === ing.ingredient_id}
                            onChange={(e) => setMergeIntoId(e.target.value)}
                            className="text-purple-600"
                          />
                          <span className="text-sm text-gray-900">{ing.ingredient_name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing || (approveAction === 'merge_into_existing' && !mergeIntoId)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Check size={18} />
                      {approveAction === 'approve_as_new' ? 'Create New' : 'Merge'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && selectedProposal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowRejectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Reject Proposal</h3>
                <button onClick={() => setShowRejectModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  <strong>Rejecting:</strong> {selectedProposal.proposed_name}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <X size={18} />
                      Reject
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IngredientProposals;
